/**
 * Branded domain primitives for the model-finder frontend (ADR-0011).
 *
 * Nominal typing for the confusable primitives the post-refactor client threads
 * through every layer: node ids, column positions, and the two version hashes.
 * Brands are COMPILE-TIME ONLY — every constructor is an identity cast (the
 * value is already validated by the boundary it enters through), so the emitted
 * JS is unchanged and behaviour is identical.
 *
 * `Level` is deliberately NOT branded — the `1|…|6` literal union is already
 * non-assignable from a bare `number` and supports the arithmetic/comparison the
 * code does (see ADR-0011 §3). `lang`/`depth` are not branded either (no sibling
 * value to confuse them with).
 *
 * One brand mechanism only: the hand-rolled `Brand<T,B>`. zod schemas apply THIS
 * brand inside a `transform` (data/wire.ts), never zod's own symbol `.brand()`,
 * so a wire `NodeId` and a DOM `nodeId(...)` are the SAME type.
 *
 * @see docs/decisions/0011-model-finder-branded-domain-primitives.md
 * @see docs/speed-optimization/research/2026-06-07 - model-finder-column-plan-and-modal/research/branded-domain-primitives.md
 */

/** Nominal-typing brand: a phantom tag the compiler tracks and the runtime ignores. */
export type Brand<T, B extends string> = T & { readonly __brand: B };

// --- Registry: every branded primitive + its boundary constructor, in ONE place. ---

/**
 * A catalog node's identity (Node.id / parentId / selectedId / requestedId / Map keys).
 *
 * TWO runtime shapes, deliberately (W0-4 public-id URLs, spec §3.4):
 *   - a **number** — the legacy `alt` data source (incl. NEGATIVE virtual series
 *     ids such as `-1`) and the virtual root `0` ({@link ROOT_ID});
 *   - a **22-char base64url string** (`[A-Za-z0-9_-]{22}`) — the `datte` source's
 *     public id, which replaces the internal `kCategory` integer on the wire.
 * The browser never knows which source is active, so every boundary accepts both
 * and sends back exactly what it received; the server decides (an id of the wrong
 * kind for its source → 404 = "no seed"). Never `Number()` a node id — parse it
 * with {@link parseNodeId}, the client-side twin of the server's `SegmentScanner`
 * rule.
 *
 * @see ersatzteil-scraper docs/plans/2026-09-15 - interactive-shop-foundation-review/wave-0/W0-4-public-id-urls.md §3.4
 */
export type NodeId = Brand<number | string, 'NodeId'>;
/** A column index in the current plan (view position, NOT a level). */
export type Position = Brand<number, 'Position'>;
/** The `/subtree` version stamp (`v11:<hex>`); feeds TreeService + every subtree fetch. */
export type SubtreeHash = Brand<string, 'SubtreeHash'>;
/** The `/levels` version stamp (`v11:<hex>`); feeds only the levels fetch. */
export type LevelsHash = Brand<string, 'LevelsHash'>;

/**
 * Brand a raw node id. "Valid by construction": the value already passed the
 * boundary's validation (zod / parseId / server-rendered data-id), so this is a
 * type-only cast, not a runtime check.
 */
export const nodeId = (n: number | string): NodeId => n as NodeId;

/** A legacy numeric id (optionally negative — `alt`'s virtual series nodes). */
const NUMERIC_ID = /^-?\d+$/;
/** A `datte` public id: exactly 22 base64url characters (no padding, no `+`/`/`). */
export const PUBLIC_ID_PATTERN = /^[A-Za-z0-9_-]{22}$/;

/** Whether `id` is a `datte` public id (string) rather than a legacy numeric id. */
export const isPublicNodeId = (id: NodeId): id is NodeId & string => typeof id === 'string';

/**
 * THE one parse rule for a node id read from text (URL param, `data-preselect`,
 * a DOM `data-id` / `<option value>`, a legacy storage cell) — mirrors the
 * server's `SegmentScanner::nodeId()` (spec §3.4):
 *   `^-?\d+$` → number, `^[A-Za-z0-9_-]{22}$` → string (as-is), anything else → `null`.
 * Never returns `NaN`; callers treat `null` exactly like the old `NaN` ("ignore").
 * Shape-only: whether a public id is a real/valid one is the server's call.
 */
export function parseNodeId(raw: string | null | undefined): NodeId | null {
  if (raw === null || raw === undefined) return null;
  if (NUMERIC_ID.test(raw)) return nodeId(Number(raw));
  if (PUBLIC_ID_PATTERN.test(raw)) return nodeId(raw);
  return null;
}

/** Brand a column index (e.g. a loop index `position(i)`). Valid by construction. */
export const position = (n: number): Position => n as Position;

/** Brand the subtree version stamp (from `data-mf-subtree-hash` / `Response.url`). */
export const subtreeHash = (s: string): SubtreeHash => s as SubtreeHash;

/** Brand the levels version stamp (from `data-mf-levels-hash`). */
export const levelsHash = (s: string): LevelsHash => s as LevelsHash;

/**
 * The virtual root id: `subtree(0)` returns the top-level nodes (manufacturers).
 * Pre-branded once so callers never re-cast it. (Moved here from tree-service.ts.)
 */
export const ROOT_ID = nodeId(0);

/*
 * GOTCHA — arithmetic erases the brand. `someNodeId + 1` / `pos - 1` compile (a
 * `Brand<number,…>` is still `number` under `+`/`-`) and yield a plain `number`.
 * Never re-cast an arithmetic result back to a brand implicitly: if a "next
 * position" is genuinely needed, write `position(p + 1)` so the re-brand is
 * explicit and reviewable. In practice ids are never arithmetic'd, and the one
 * position-arithmetic site (`levelAtPosition`/`position - 1`) *should* drop to a
 * plain `number`/`Level` — that is correct (the result is no longer a position).
 * Since W0-4 a NodeId may be a STRING, so `+` on one would even concatenate —
 * one more reason never to do arithmetic or ordering comparisons on node ids.
 */
